import React, { useState, useEffect, Suspense } from 'react';
import { Canvas } from "@react-three/fiber";
import { View, Environment, OrbitControls, PerspectiveCamera, ContactShadows } from "@react-three/drei";
import api from '@/lib/api';
import GenericModel from "./GenericModel";
import { yellowImg } from "../utils";
import * as THREE from 'three';
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { animateWithGsap } from '../utils/animations';

const Model = () => {
  const [models, setModels] = useState([]);
  const [activeModel, setActiveModel] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const { data } = await api.get('/models');
        if (data && data.length > 0) {
          const baseUrl = import.meta.env.VITE_API_URL.replace('/api', '');
          const activeModels = data.filter(m => m.isActive).map(m => ({
            ...m,
            path: m.path ? `${baseUrl}${m.path}` : null,
            img: m.thumbnail ? `${baseUrl}${m.thumbnail}` : yellowImg,
          }));

          if (activeModels.length > 0) {
            setModels(activeModels);
            setActiveModel(activeModels[0]);
          } else {
            setPlaceholderModels();
          }
        } else {
          setPlaceholderModels();
        }
      } catch (error) {
        console.error("Failed to fetch models", error);
        setPlaceholderModels();
      } finally {
        setLoading(false);
      }
    };

    const setPlaceholderModels = () => {
      const placeholders = [
        {
          _id: 'p1',
          name: 'Curriculum Relevant',
          title: 'Curriculum Relevant Training',
          description: 'SNC training is meticulously designed to be curriculum relevant, ensuring that every module aligns with academic and professional standards for maximum impact.',
          path: '/cap2.glb',
          isActive: true,
          scale: 0.8,
          rotation: [Math.PI, 0, 0],
          position: [0, 0, 0],
          material: { color: '#4cc3e5', metalness: 0.8, roughness: 0.2 }
        },
        {
          _id: 'p2',
          name: 'Global',
          title: 'Accessible Everywhere',
          description: 'Experience training without borders. SNC Global ensures our AI training modules are accessible from anywhere in the world, at any time.',
          path: '/GLOBE.gltf',
          isActive: true,
          scale: 0.5,
          rotation: [0, 0, 0],
          position: [0, 0, 0],
          material: { color: '#4cc3e5', metalness: 0.8, roughness: 0.2 }
        },
        {
          _id: 'p3',
          name: 'Business',
          title: 'Corporate Oriented',
          description: 'Tailored for the modern enterprise. Learn how to seamlessly incorporate AI into business processes and corporate environments for a competitive edge.',
          path: '/GRAPH.gltf',
          isActive: true,
          scale: 0.2,
          rotation: [0, 0, 0],
          position: [0, 0, 0],
          material: { color: '#4cc3e5', metalness: 0.8, roughness: 0.2 }
        }
      ];
      setModels(placeholders);
      setActiveModel(placeholders[0]);
    };
    fetchModels();
  }, []);

  useGSAP(() => {
    animateWithGsap('#model-heading', { y: 0, opacity: 1 });
  }, []);

  if (loading) return <section className="common-padding transition-colors duration-300 bg-white dark:bg-zinc"><div className="text-black dark:text-white text-center">Loading 3D Experience...</div></section>;
  if (!activeModel) return <section className="common-padding transition-colors duration-300 bg-white dark:bg-zinc"><div className="text-black dark:text-white text-center">No 3D Models Available</div></section>;

  return (
    <section id="model" className="common-padding relative w-full h-screen overflow-hidden bg-white dark:bg-zinc transition-colors duration-300">
      {/* FULL SCREEN CANVAS */}
      <div className="absolute top-0 left-0 w-full h-full z-0">
        <Canvas
          shadows
          camera={{ position: [0, 0, 8], fov: 45 }}
          className="w-full h-full"
          style={{ position: 'absolute', top: 0, left: 0, touchAction: 'pan-y' }}
        >
          <ambientLight intensity={activeModel.lighting?.ambientIntensity || 1.5} />
          <directionalLight
            position={[
              activeModel.lighting?.directionalPosition?.x || 5,
              activeModel.lighting?.directionalPosition?.y || 10,
              activeModel.lighting?.directionalPosition?.z || 7
            ]}
            intensity={activeModel.lighting?.directionalIntensity || 2}
            castShadow
          />
          <Environment preset="city" />
          <ContactShadows resolution={1024} scale={20} blur={2} opacity={0.5} far={10} color="#000000" />

          <Suspense fallback={null}>
            <group position={[0, -1.5, 0]}>
              {activeModel.path && (
                <GenericModel
                  url={activeModel.path}
                  scale={activeModel.scale}
                  rotation={activeModel.rotation}
                  position={activeModel.position}
                  materialConfig={activeModel.material}
                />
              )}
            </group>
          </Suspense>
          <OrbitControls 
            enableZoom={false} 
            enablePan={false} 
            autoRotate={true} 
            autoRotateSpeed={0.5}
            minPolarAngle={Math.PI / 2}
            maxPolarAngle={Math.PI / 2}
          />
        </Canvas>
      </div>

      {/* OVERLAY CONTENT */}
      <div className="screen-max-width relative z-10 w-full h-full flex flex-col justify-between pointer-events-none">

        {/* HEADER: Title left, Description right */}
        <div className="flex flex-col md:flex-row justify-between items-start md:px-10 px-5 pointer-events-auto">
          {/* LEFT: Title */}
          <div className="flex-1 md:text-left text-center mb-6 md:mb-0">
            <h1 id="model-heading" className="section-heading text-black dark:text-white leading-tight">
              snc. <br /> Why choose snc?
            </h1>
            <h2 className="text-3xl md:text-5xl font-black text-black dark:text-white tracking-tighter mt-2">
              {activeModel.title || activeModel.name}
            </h2>
          </div>

          {/* RIGHT: Description */}
          <div className="flex-1 md:text-right text-center max-w-md ml-auto backdrop-blur-md bg-black/5 dark:bg-black/60 p-6 rounded-2xl border border-black/10 dark:border-white/10 transition-colors duration-500 shadow-xl">
            <p className="text-lg text-black dark:text-gray-200 font-medium leading-relaxed">
              {activeModel.description}
            </p>
            <button className="mt-6 px-8 py-3 bg-blue text-white rounded-full font-bold hover:bg-blue/90 transition-all shadow-lg hover:scale-105 active:scale-95">
              Learn more
            </button>
          </div>
        </div>

        {/* BOTTOM: Selector Bar (Like Video Carousel) */}
        <div className="flex-center pb-10 pointer-events-auto">
          <div className="flex-center py-3 px-5 bg-white/50 dark:bg-gray-300 backdrop-blur rounded-full gap-3 border border-black/5 dark:border-white/5 transition-colors duration-300">
            {models.map((m) => (
              <button
                key={m._id}
                className={`text-sm font-semibold transition-all px-4 py-2 rounded-full ${activeModel._id === m._id
                  ? 'bg-blue text-white shadow-lg'
                  : 'text-black dark:text-gray hover:text-blue'
                  }`}
                onClick={() => setActiveModel(m)}
              >
                {m.name}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Model;